// src/pages/widgets/StatusPicker.jsx
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSystemDashboardTenantStatus as useTenantStatus } from "../../hooks";
import styles from "./Widget.module.css";

const META = {
    WAITING: {
        label: "대기",
        chip: `${styles.chip} ${styles["chip--waiting"]}`,
        btn: `${styles["btn--waiting"]}`,
    },
    APPROVED: {
        label: "승인",
        chip: `${styles.chip} ${styles["chip--approved"]}`,
        btn: `${styles["btn--approved"]}`,
    },
    PENDING: {
        label: "보류",
        chip: `${styles.chip} ${styles["chip--pending"]}`,
        btn: `${styles["btn--pending"]}`,
    },
    REJECTED: {
        label: "거절",
        chip: `${styles.chip} ${styles["chip--rejected"]}`,
        btn: `${styles["btn--rejected"]}`,
    },
};

const ORDER = ["WAITING", "PENDING", "REJECTED", "APPROVED"];
const CANON = {
    APPROVED: "APPROVED",
    APROVED: "APPROVED",
    APPROVE: "APPROVED",
    WAITING: "WAITING",
    PENDING: "PENDING",
    REJECTED: "REJECTED",
};

/** TenantDetailModal 배지 팔레트와 맞춘 테마 */
const THEME = {
    WAITING: {
        hero: "#fdba74",
        hero2: "#fdba74",
        accent: "#9a3412",
    },
    APPROVED: {
        hero: "#22c55e",
        hero2: "#22c55e",
        accent: "#065f46",
    },
    PENDING: {
        hero: "#60a5fa",
        hero2: "#60a5fa",
        accent: "#1d4ed8",
    },
    REJECTED: {
        hero: "#ef4444",
        hero2: "#ef4444",
        accent: "#991b1b",
    },
};

export default function StatusPicker({ id, status }) {
    const [open, setOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [choice, setChoice] = useState(null);

    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const { mutate, isPending } = useTenantStatus();

    const upper = CANON[String(status ?? "").trim().toUpperCase()] ?? String(status ?? "").trim().toUpperCase();
    const meta = META[upper] ?? META.WAITING;

    const [pos, setPos] = useState({ top: 0, left: 0 });

    const updateMenuPos = () => {
        const t = triggerRef.current, m = menuRef.current;
        if (!t || !m) return;
        const r = t.getBoundingClientRect();
        const menuW = m.offsetWidth || 180, menuH = m.offsetHeight || 40;
        const gap = 6, vw = window.innerWidth, vh = window.innerHeight;
        let left = r.left, top = r.bottom + gap;
        if (left + menuW > vw - 8) left = Math.max(8, r.right - menuW);
        if (left < 8) left = 8;
        if (top + menuH > vh - 8) {
            const alt = r.top - gap - menuH;
            top = alt >= 8 ? alt : Math.max(8, Math.min(vh - menuH - 8, top));
        }
        setPos({ top, left });
    };

    useLayoutEffect(() => {
        if (open) updateMenuPos();
    }, [open]);

    useEffect(() => {
        if (!open) return;

        const onScroll = () => updateMenuPos();
        const onResize = () => updateMenuPos();
        const handleClickOutside = (e) => {
            if ( menuRef.current && !menuRef.current.contains(e.target) && triggerRef.current && !triggerRef.current.contains(e.target) ) {
                setOpen(false);
            }
        };

        window.addEventListener("scroll", onScroll, true);
        window.addEventListener("resize", onResize);

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("touchstart", handleClickOutside);

        return () => {
            window.removeEventListener("scroll", onScroll, true);
            window.removeEventListener("resize", onResize);
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        };
    }, [open]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") {
                if (confirmOpen) {
                    setConfirmOpen(false);
                    setChoice(null);
                } else if (open) {
                    setOpen(false);
                }
            }
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, confirmOpen]);

    const openConfirm = (next) => {
        const canon = CANON[String(next).trim().toUpperCase()] ?? String(next).trim().toUpperCase();
        setChoice(canon);
        setConfirmOpen(true);
    };

    const onConfirm = () => {
        if (!choice || choice === upper) {
            setConfirmOpen(false);
            setOpen(false);
            return;
        }
        mutate(
            { id, status: choice },
            {
                onSuccess: () => {
                    setConfirmOpen(false);
                    setOpen(false);
                    setChoice(null);
                },
            }
        );
    };
    const onCancel = () => {
        setConfirmOpen(false);
        setChoice(null);
    };

    /** 🔹 확인 모달용 테마 (선택 상태 기준, 없으면 현재 상태 기준) */
    const themeKey = choice || upper || "WAITING";
    const theme = THEME[themeKey] || THEME.WAITING;

    return (
        <>
            {/* 트리거 버튼 (리스트 안 칩 스타일과 동일 팔레트) */}
            <button
                ref={triggerRef}
                type="button"
                className={`${styles.statuspicker__trigger} ${meta.chip}`}
                onClick={() => setOpen((v) => !v)}
                disabled={isPending}
                aria-haspopup="menu"
                aria-expanded={open}
                title="상태 변경"
            >
                {meta.label}
                <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden>
                    <path
                        d="M6 9l6 6 6-6"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                    />
                </svg>
            </button>

            {/* 드롭다운 메뉴 */}
            {open &&
                createPortal(
                    <div
                        ref={menuRef}
                        role="menu"
                        className={styles.statuspicker__menu}
                        style={{ top: pos.top, left: pos.left }} // 위치만 동적
                    >
                        {ORDER.map((s) => {
                            const m = META[s];
                            const isCurrent = s === upper;
                            return (
                                <button
                                    key={s}
                                    type="button"
                                    role="menuitemradio"
                                    aria-checked={isCurrent}
                                    className={`${styles.statuspicker__item} ${m.btn} ${
                                        isCurrent ? "is-current" : ""
                                    }`}
                                    onClick={() => openConfirm(s)}
                                    disabled={isPending}
                                    title={m.label}
                                >
                                    {m.label}
                                </button>
                            );
                        })}
                    </div>,
                    document.body
                )}

            {/* 상태 변경 확인 모달 */}
            {confirmOpen &&
                createPortal(
                    <div className={styles.spOverlay}>
                        {/* 배경 */}
                        <div
                            aria-hidden
                            className={styles.spOverlayBg}
                        />

                        {/* 카드 */}
                        <div
                            role="dialog"
                            aria-label="상태 변경 확인"
                            className={styles.spCard}
                            style={{
                                "--sp-hero": theme.hero,
                                "--sp-accent": theme.accent,
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* 헤더 */}
                            <div className={styles.spCardHead}>
                                <strong className={styles.spCardHeadTitle}>
                                    상태 변경
                                </strong>
                            </div>

                            {/* 본문 */}
                            <div className={styles.spBody}>
                                <div className={styles.spBodyText}>
                                    이 테넌트의 상태를{" "}
                                    <b className={styles.spAccent}>
                                        {META[choice]?.label}
                                    </b>
                                    (으)로 변경하시겠습니까?
                                </div>

                                {/* 현재 → 변경 상태 뱃지 라인 */}
                                <div className={styles.spStateFlow}>
                                    <span className={META[upper]?.chip}>
                                        {META[upper]?.label}
                                    </span>
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        aria-hidden
                                        className={styles.spArrow}
                                    >
                                        <path
                                            fill="currentColor"
                                            d="M10 17l5-5-5-5v10z"
                                        />
                                    </svg>
                                    <span className={META[choice]?.chip}>
                                        {META[choice]?.label}
                                    </span>
                                </div>
                            </div>

                            {/* 액션 버튼 */}
                            <div className={styles.spActions}>
                                <button
                                    type="button"
                                    className={`${styles.spBtn} ${styles.spBtnPrimary}`}
                                    onClick={onConfirm}
                                    disabled={isPending}
                                >
                                    확인
                                </button>
                                <button
                                    type="button"
                                    className={styles.spBtn}
                                    onClick={onCancel}
                                    disabled={isPending}
                                >
                                    취소
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
        </>
    );
}
